/**
 * Change Location Modal
 *
 * Allows CRM users to switch active location without re-login.
 * Calls /api/auth/crm-change-location, updates JWT and context.
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const LOCATIONS = [
  { id: 1, name: 'Brampton', color: '#C8614A' },
  { id: 2, name: 'Etobicoke', color: '#2B8FAD' },
  { id: 3, name: 'Mississauga', color: '#1A2332' },
  { id: 4, name: 'Springdale', color: '#5A7A52' }
];

function ChangeLocationModal({ isOpen, onClose }) {
  const { activeLocation, changeLocation } = useAuth();
  const [selectedId, setSelectedId] = useState(activeLocation?.id || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedId || selectedId === activeLocation?.id) {
      onClose();
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await changeLocation(Number(selectedId));
      if (result.success) {
        onClose();
      } else {
        setError(result.error || 'Failed to change location');
      }
    } catch (err) {
      setError('Failed to change location. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'white', borderRadius: '14px',
        width: '100%', maxWidth: '380px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        animation: 'clmSlideIn 0.2s ease-out both'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Change Location
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9ca3af', fontSize: '18px', padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
              background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: '13px'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {LOCATIONS.map((loc) => {
              const isActive = loc.id === activeLocation?.id;
              const isSelected = loc.id === Number(selectedId);

              return (
                <button
                  key={loc.id}
                  onClick={() => setSelectedId(loc.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', borderRadius: '10px',
                    border: isSelected ? `2px solid ${loc.color}` : '2px solid #e5e7eb',
                    background: isSelected ? `${loc.color}08` : 'white',
                    cursor: 'pointer', transition: 'all 0.15s ease',
                    width: '100%', textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '12px', height: '12px', borderRadius: '50%',
                    background: loc.color, flexShrink: 0
                  }} />
                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827', flex: 1 }}>
                    {loc.name}
                  </span>
                  {isActive && (
                    <span style={{
                      fontSize: '11px', color: '#6b7280', background: '#f3f4f6',
                      padding: '2px 8px', borderRadius: '4px', fontWeight: 500
                    }}>
                      Current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', gap: '8px', justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', background: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: '8px', fontSize: '14px',
              fontWeight: 500, cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedId || selectedId === activeLocation?.id}
            style={{
              padding: '10px 20px',
              background: submitting || !selectedId || selectedId === activeLocation?.id ? '#94a3b8' : '#2563eb',
              color: 'white', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: 600,
              cursor: submitting || !selectedId || selectedId === activeLocation?.id ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Switching...' : 'Switch Location'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes clmSlideIn {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

export default ChangeLocationModal;
